import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  assertNever,
  getControlCategoryName,
  getLocalUTCOffset,
  getPartnerEventName,
  service,
  useAuthConfig,
  userLocalShortTimeZone,
} from "@theprelude/core";
import {
  Button,
  CheckboxFormField,
  Collapsible,
  CollapsibleContent,
  DurationInputFormField,
  Form,
  InputFormField,
  notifyError,
  notifySuccess,
  SelectFormField,
  TextAreaFormField,
  ToggleButtonFormField,
  TrashcanIcon,
} from "@theprelude/ds";
import {
  ControlCategories,
  ControlCategory,
  Notification,
  PartnerEventCode,
  PartnerEvents,
  RunCode,
  RunCodes,
  ValidRunCodes,
} from "@theprelude/sdk";
import isEqual from "lodash/isEqual";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z, ZodIssueCode } from "zod";
import { Filter } from "@/lib/filter";
import { getFrequency } from "@/lib/frequency";
import {
  getOdataId,
  isDurationFilter,
  OdataDefinition,
  parseFilter,
} from "@/lib/odata";
import {
  AdditionalFilter,
  anyDurationLastSeenFilter,
  anyDomainFilter,
  anyLastSeenFilter,
  anyOwnershipFilter,
  anyPlatformFilter,
  anyPrincipalNameFilter,
  anyRiskFilter,
  anyStatusFilter,
  convertDurationAdditionalFilter,
  convertDurationFilter,
  emailFilter,
  hostNameFilter,
  serialNumberFilter,
  startTimeFilter,
  startTimeOdata,
  anyDeviceTypeFilter,
  anyInboxTypeFilter,
  anyUserTypeFilter,
  anyCloudRegionFilter,
  cloudAccountIdFilter,
  cloudInstanceIdFilter,
  macAddressFilter,
  userDevicesFilter,
} from "../filter-form/additional-filters";
import {
  convertFiltersToFormFilters,
  convertFormFiltersToFilters,
  FilterFormActions,
  FilterFormFields,
  FormFilter,
  formFilterSchema,
} from "../filter-form/filter-form";
import { getOdataFilters } from "../filter-form/odata";
import { DeleteNotificationModal } from "./delete-notification-modal";

const validRunCodes: RunCode[] = [
  RunCodes.DAILY,
  RunCodes.WEEKLY,
  RunCodes.MONTHLY,
  RunCodes.MONDAY,
  RunCodes.TUESDAY,
  RunCodes.WEDNESDAY,
  RunCodes.THURSDAY,
  RunCodes.FRIDAY,
  RunCodes.SATURDAY,
  RunCodes.SUNDAY,
  RunCodes.MONTH_1,
];

const formSchema = z
  .object({
    id: z.string().optional(),
    controlCategory: z.nativeEnum(ControlCategories).superRefine((val, ctx) => {
      if (val === ControlCategories.INVALID) {
        ctx.addIssue({
          code: ZodIssueCode.invalid_enum_value,
          message: "Object is required",
          received: val,
          options: [],
        });
      }
    }),
    partnerEvent: z.nativeEnum(PartnerEvents).superRefine((val, ctx) => {
      if (val === PartnerEvents.INVALID) {
        ctx.addIssue({
          code: ZodIssueCode.invalid_enum_value,
          message: "Control Failure State is required",
          received: val,
          options: [],
        });
      }
    }),
    secondsInState: z
      .number()
      .gte(0, { message: "Time in State cannot be negative" }),
    filters: z.array(formFilterSchema),
    frequency: z.nativeEnum(ValidRunCodes),
    scheduledHour: z.number().int().min(0).max(23),
    sendEmail: z.boolean(),
    sendSlack: z.boolean(),
    sendTeams: z.boolean(),
    emailRecipients: z.string().optional(),
    emailSubject: z.string().optional(),
    emailMessage: z.string().optional(),
    slackWebhookUrls: z.string().optional(),
    slackMessage: z.string().optional(),
    teamsWebhookUrls: z.string().optional(),
    teamsMessage: z.string().optional(),
    suppressEmpty: z.boolean(),
  })
  .refine(
    (input) =>
      !input.sendEmail ||
      /^\s*\S+@\S+\.\S+(\s*,\s*\S+@\S+\.\S+)*\s*$/.test(
        input.emailRecipients ?? "",
      ),
    {
      message:
        "At least one recipient is required and must be a valid email address",
      path: ["emailRecipients"],
    },
  )
  .refine((input) => !input.sendEmail || !!input.emailSubject?.trim(), {
    message: "Subject is required",
    path: ["emailSubject"],
  })
  .refine((input) => !input.sendEmail || !!input.emailMessage?.trim(), {
    message: "Message is required",
    path: ["emailMessage"],
  })
  .refine((input) => !input.sendSlack || !!input.slackWebhookUrls?.trim(), {
    message: "At least one webhook URL is required",
    path: ["slackWebhookUrls"],
  })
  .refine((input) => !input.sendSlack || !!input.slackMessage?.trim(), {
    message: "Message is required",
    path: ["slackMessage"],
  })
  .refine((input) => input.sendEmail || input.sendSlack || input.sendTeams, {
    message: "At least one integration target is required",
    // TODO: I'd prefer to add this error to the root, but that doesn't seem to be working.
    // REF: https://github.com/colinhacks/zod/issues/3919
    path: ["sendTeams"],
  });

type FormSchema = z.infer<typeof formSchema>;

export const formatHour = (hour: number) =>
  `${hour % 12 === 0 ? 12 : hour % 12}:00${hour / 12 >= 1 ? "pm" : "am"} ${userLocalShortTimeZone}`;

const controlCategoryOptions: { id: ControlCategory; name: string }[] = [
  ControlCategories.XDR,
  ControlCategories.ASSET_MANAGER,
  ControlCategories.EMAIL,
  ControlCategories.IDENTITY,
  ControlCategories.VULN_MANAGER,
]
  .map((category) => ({ id: category, name: getControlCategoryName(category) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const scheduledHourOptions: { id: number; name: string }[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23,
].map((n) => ({ id: n, name: formatHour(n) }));

const defaultValues: FormSchema = {
  controlCategory: ControlCategories.INVALID,
  partnerEvent: PartnerEvents.INVALID,
  secondsInState: 0,
  filters: [],
  frequency: ValidRunCodes.DAILY,
  scheduledHour: 9,
  sendEmail: false,
  sendSlack: false,
  sendTeams: false,
  emailRecipients: "",
  emailSubject: "",
  emailMessage: "",
  slackMessage: "",
  slackWebhookUrls: "",
  teamsMessage: "",
  teamsWebhookUrls: "",
  suppressEmpty: true,
};

const endpointFilters: AdditionalFilter[] = [
  anyOwnershipFilter,
  anyDeviceTypeFilter,
  anyDurationLastSeenFilter,
  anyLastSeenFilter,
  anyPlatformFilter,
  anyCloudRegionFilter,
  hostNameFilter,
  serialNumberFilter,
  cloudInstanceIdFilter,
  cloudAccountIdFilter,
  macAddressFilter,
];

const inboxFilters: AdditionalFilter[] = [
  anyInboxTypeFilter,
  emailFilter,
  anyDomainFilter,
  anyPrincipalNameFilter,
];

const userFilters: AdditionalFilter[] = [
  anyUserTypeFilter,
  emailFilter,
  userDevicesFilter,
  anyDurationLastSeenFilter,
  anyLastSeenFilter,
  anyStatusFilter,
  anyRiskFilter,
];

export const allNotificationFilters: AdditionalFilter[] = [
  ...endpointFilters,
  ...inboxFilters,
  ...userFilters,
  ...Object.values(PartnerEvents).map((event): AdditionalFilter => {
    const odata: OdataDefinition = {
      ...startTimeOdata,
      with: { ...startTimeOdata.with, event },
    };
    return convertDurationAdditionalFilter(
      {
        ...startTimeFilter,
        id: getOdataId(odata),
        odata,
      },
      odata,
    );
  }),
];

const getAvailableFilters = (category: ControlCategory): AdditionalFilter[] => {
  switch (category) {
    case ControlCategories.ASSET_MANAGER:
    case ControlCategories.DISCOVERED_DEVICES:
    case ControlCategories.VULN_MANAGER:
    case ControlCategories.XDR: {
      return endpointFilters;
    }
    case ControlCategories.IDENTITY: {
      return userFilters;
    }
    case ControlCategories.EMAIL: {
      return inboxFilters;
    }
    case ControlCategories.CLOUD:
    case ControlCategories.INVALID:
    case ControlCategories.NETWORK:
    case ControlCategories.PRIVATE_REPO:
    case ControlCategories.SIEM: {
      return [];
    }
    default: {
      return assertNever(category);
    }
  }
};

const getControlFailureStates = (category: ControlCategory) => {
  switch (category) {
    case ControlCategories.ASSET_MANAGER: {
      return getEventList(PartnerEvents.NO_ASSET_MANAGER);
    }
    case ControlCategories.XDR: {
      return getEventList(
        PartnerEvents.MISSING_AV_POLICY,
        PartnerEvents.MISSING_EDR_POLICY,
        PartnerEvents.NO_EDR,
        PartnerEvents.REDUCED_FUNCTIONALITY_MODE,
        PartnerEvents.MISCONFIGURED_POLICY_SETTING,
      );
    }
    case ControlCategories.IDENTITY: {
      return getEventList(
        PartnerEvents.MISSING_MFA,
        PartnerEvents.USER_MISSING_ASSET_MANAGER,
        PartnerEvents.USER_MISSING_EDR,
        PartnerEvents.USER_MISSING_VULN_MANAGER,
        PartnerEvents.MISCONFIGURED_POLICY_SETTING,
      );
    }
    case ControlCategories.EMAIL: {
      return getEventList(PartnerEvents.MISCONFIGURED_POLICY_SETTING);
    }
    case ControlCategories.VULN_MANAGER: {
      return getEventList(
        PartnerEvents.MISSING_SCAN,
        PartnerEvents.OUT_OF_DATE_SCAN,
      );
    }
    case ControlCategories.CLOUD:
    case ControlCategories.DISCOVERED_DEVICES:
    case ControlCategories.INVALID:
    case ControlCategories.NETWORK:
    case ControlCategories.PRIVATE_REPO:
    case ControlCategories.SIEM: {
      return [];
    }
    default: {
      return assertNever(category);
    }
  }
};

const getEventList = (...events: PartnerEventCode[]) =>
  events
    .map((event) => ({
      id: event,
      name: getPartnerEventName(event),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

function getEventFilterIndex(filters: Filter[]) {
  const isLegacyInStateFilter = filters.some((f) =>
    f.id.includes("seconds_in_state"),
  );

  // This is necessary until support of seconds_in_state is no longer needed
  if (isLegacyInStateFilter) {
    return filters.findIndex(
      (f) =>
        f.scope === "failures" &&
        f.id.includes("seconds_in_state") &&
        f.with &&
        isEqual(Object.keys(f.with), ["event"]),
    );
  }

  return filters.findIndex(
    (f) =>
      f.scope === "failures" &&
      isDurationFilter(f) &&
      f.with &&
      isEqual(Object.keys(f.with), ["event"]),
  );
}

const getDefaultValues = (
  notification: Notification | undefined,
): FormSchema => {
  if (!notification) {
    return defaultValues;
  }

  const filters: Filter[] = notification.filter
    ? parseFilter(notification.filter, allNotificationFilters)
    : [];

  const eventFilterIndex = getEventFilterIndex(filters);

  const filteredFilters = filters.filter(
    (f) => !f.id.includes("seconds_in_state"),
  );

  const formFilters = convertFiltersToFormFilters(
    filteredFilters,
    allNotificationFilters,
  );

  const eventFilter = filters[eventFilterIndex];
  const eventFormFilter = formFilters[eventFilterIndex];

  if (eventFilterIndex !== -1) {
    formFilters.splice(eventFilterIndex, 1);
  }

  const partnerEvent = eventFilter?.with?.event ?? PartnerEvents.INVALID;
  const secondsInState = Number.parseInt(eventFormFilter?.value ?? "0", 10);

  return {
    id: notification.id,
    controlCategory: notification.control_category,
    partnerEvent,
    secondsInState,
    filters: formFilters,
    frequency: notification.run_code,
    scheduledHour: notification.scheduled_hour + getLocalUTCOffset(),
    sendEmail: !!notification.email,
    sendSlack: !!notification.slack,
    sendTeams: !!notification.teams,
    emailRecipients: notification.email?.emails.join(", ") ?? "",
    emailSubject: notification.email?.subject ?? "",
    emailMessage: notification.email?.message ?? "",
    slackWebhookUrls: notification.slack?.hook_urls.join(", ") ?? "",
    slackMessage: notification.slack?.message ?? "",
    teamsWebhookUrls: notification.teams?.hook_urls.join(", ") ?? "",
    teamsMessage: notification.teams?.message ?? "",
    suppressEmpty: notification.suppress_empty,
  };
};

const getCommaDelimitedValues = (str: string) =>
  str.split(/\s*,\s*/).filter(Boolean);

const useSaveNotification = (availableFilters: AdditionalFilter[]) => {
  const authConfig = useAuthConfig();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: FormSchema) => {
      if (data.controlCategory === ControlCategories.INVALID) {
        throw new Error("Object is required");
      }
      if (data.partnerEvent === PartnerEvents.INVALID) {
        throw new Error("Control Failure State is required");
      }

      const formFilters: FormFilter[] = [...data.filters];
      const filters = [
        ...convertFormFiltersToFilters(formFilters, availableFilters),
        convertDurationFilter(
          { ...startTimeOdata, with: { event: data.partnerEvent } },
          "less than",
          data.secondsInState,
        ),
      ];

      return service(authConfig).scm.saveNotification({
        id: data.id,
        event: data.partnerEvent,
        run_code: data.frequency,
        scheduled_hour: data.scheduledHour - getLocalUTCOffset(),
        control_category: data.controlCategory,
        slack: data.sendSlack
          ? {
              hook_urls: getCommaDelimitedValues(data.slackWebhookUrls ?? ""),
              message: data.slackMessage ?? "",
            }
          : null,
        teams: data.sendTeams
          ? {
              hook_urls: getCommaDelimitedValues(data.teamsWebhookUrls ?? ""),
              message: data.teamsMessage ?? "",
            }
          : null,
        email: data.sendEmail
          ? {
              emails: getCommaDelimitedValues(data.emailRecipients ?? ""),
              message: data.emailMessage ?? "",
              subject: data.emailSubject ?? "",
            }
          : null,
        filter: getOdataFilters(filters),
        suppress_empty: data.suppressEmpty,
      });
    },
    onSuccess: (_data, variables) => {
      notifySuccess({
        message: `Notification has been ${variables.id ? "updated" : "created"}.`,
      });
      void navigate({ to: "/account/notifications" });
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to save this notification.",
        error,
      });
    },
  });
};

const useDeleteNotification = (
  setShowDeletingConfirmation: (show: boolean) => void,
) => {
  const authConfig = useAuthConfig();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) =>
      service(authConfig).scm.deleteNotification(id ?? ""),
    onSuccess: () => {
      setShowDeletingConfirmation(false);
      notifySuccess({ message: "Notification has been deleted." });
      return navigate({ to: "/account/notifications" });
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to delete this notification.",
        error,
      });
    },
  });
};

export const NotificationForm = ({
  notification,
}: {
  notification?: Notification;
}) => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(notification),
  });

  const { control, register, setValue, watch } = form;

  const availableFilters = getAvailableFilters(watch("controlCategory"));
  const controlFailureStates = getControlFailureStates(
    watch("controlCategory"),
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });

  const [showDeleteConfirmation, setShowDeletingConfirmation] = useState(false);

  const { mutate: saveNotification, isPending: isSaving } =
    useSaveNotification(availableFilters);

  const { mutate: deleteNotification, isPending: isDeleting } =
    useDeleteNotification(setShowDeletingConfirmation);

  const isPending = isSaving || isDeleting;

  const isValidControlCategory =
    watch("controlCategory") !== ControlCategories.INVALID;

  const isValidPartnerEvent = watch("partnerEvent") !== PartnerEvents.INVALID;

  const onSubmit = (data: FormSchema) => saveNotification(data);

  return (
    <Form {...form} className="flex flex-col gap-4" onSubmitValid={onSubmit}>
      <div className="grid w-full grid-cols-1 items-start gap-2 md:grid-cols-6">
        <SelectFormField
          {...register("controlCategory", {
            onChange: (event) => {
              setValue("controlCategory", event.target.value);
              setValue("partnerEvent", PartnerEvents.INVALID);
            },
          })}
          label="Object"
          size="sm"
          readOnly={isPending}
          required
          options={[
            {
              label: "Select an Object",
              value: ControlCategories.INVALID,
              disabled: true,
            },
            ...controlCategoryOptions.map(({ id, name }) => ({
              label: name,
              value: id,
            })),
          ]}
          className="col-span-2"
        />
        <SelectFormField
          {...register("partnerEvent")}
          label="Control Failure State"
          size="sm"
          readOnly={isPending || !isValidControlCategory}
          required
          options={[
            {
              label: "Select a Control Failure State",
              value: PartnerEvents.INVALID,
              disabled: true,
            },
            ...controlFailureStates.map(({ id, name }) => ({
              label: name,
              value: id,
            })),
          ]}
          className="col-span-2"
        />
        <DurationInputFormField
          {...register("secondsInState")}
          label="Time in State"
          size="sm"
          readOnly={
            isPending || !isValidControlCategory || !isValidPartnerEvent
          }
          required
          allowedUnits={["days"]}
          defaultUnit="days"
          className="col-span-2"
        />
      </div>
      <FilterFormFields
        fields={fields}
        onRemove={remove}
        availableFilters={availableFilters}
        readOnly={isPending || !isValidControlCategory || !isValidPartnerEvent}
      />
      <FilterFormActions
        onAdd={append}
        onClear={() => remove()}
        disabled={isPending || !isValidControlCategory || !isValidPartnerEvent}
      />
      <div className="grid w-full grid-cols-1 items-start gap-2 md:grid-cols-6">
        <SelectFormField
          {...register("frequency")}
          label="Frequency"
          size="sm"
          readOnly={isPending}
          required
          options={validRunCodes.map((runCode) => ({
            label: getFrequency(runCode),
            value: runCode,
          }))}
          className="col-span-2"
        />
        <SelectFormField
          {...register("scheduledHour")}
          label="Time"
          size="sm"
          readOnly={isPending}
          required
          options={scheduledHourOptions.map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          className="col-span-2 xl:col-span-1"
        />
      </div>
      <div className="flex flex-col">
        <ToggleButtonFormField
          {...register("sendEmail")}
          label="Email"
          readOnly={isPending}
        />
        <Collapsible open={!!watch("sendEmail")} className="pt-2">
          <CollapsibleContent className="flex flex-col gap-4">
            <InputFormField
              {...register("emailRecipients")}
              label="Recipients (comma-separated)"
              placeholder="user@company.com"
              readOnly={isPending}
              required
            />
            <InputFormField
              {...register("emailSubject")}
              label="Subject"
              placeholder="A No EDR notification was triggered for some endpoints"
              readOnly={isPending}
              required
            />
            <TextAreaFormField
              {...register("emailMessage")}
              label="Message"
              placeholder="A No EDR notification was triggered for the list of endpoints below."
              readOnly={isPending}
              required
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
      <div className="flex flex-col">
        <ToggleButtonFormField
          {...register("sendSlack")}
          label="Slack"
          readOnly={isPending}
        />
        <Collapsible open={!!watch("sendSlack")} className="pt-2">
          <CollapsibleContent className="flex flex-col gap-4">
            <InputFormField
              {...register("slackWebhookUrls")}
              label="Webhook URLs (comma-separated)"
              placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX"
              readOnly={isPending}
              required
            />
            <TextAreaFormField
              {...register("slackMessage")}
              label="Message"
              placeholder="A No EDR notification was triggered for the list of endpoints below."
              readOnly={isPending}
              required
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
      <div className="flex flex-col">
        <ToggleButtonFormField
          {...register("sendTeams")}
          label="Teams"
          readOnly={isPending}
        />
        <Collapsible open={!!watch("sendTeams")} className="pt-2">
          <CollapsibleContent className="flex flex-col gap-4">
            <InputFormField
              {...register("teamsWebhookUrls")}
              label="Webhook URLs (comma-separated)"
              placeholder="https://XXXXX.logic.azure.com/workflows/XXXXX/triggers/manual/paths/invoke?sig=XXXXX"
              readOnly={isPending}
              required
            />
            <TextAreaFormField
              {...register("teamsMessage")}
              label="Message"
              placeholder="A No EDR notification was triggered for the list of endpoints below."
              readOnly={isPending}
              required
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
      <CheckboxFormField
        {...register("suppressEmpty")}
        label="Send notification if there are no matching results"
        readOnly={isPending}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            type="submit"
            intent="primary"
            disabled={isPending}
            loading={isSaving}
          >
            Save
          </Button>
          <Link to="/account/notifications">
            <Button type="button" intent="secondary" disabled={isPending}>
              Go back
            </Button>
          </Link>
        </div>
        {notification && (
          <Button
            type="button"
            intent="negative-inverted"
            icon={<TrashcanIcon />}
            disabled={isPending}
            loading={isDeleting}
            onClick={() => setShowDeletingConfirmation(true)}
          >
            Delete this notification
          </Button>
        )}
      </div>
      <DeleteNotificationModal
        open={showDeleteConfirmation}
        isDeleting={isDeleting}
        onConfirm={() => deleteNotification(notification?.id ?? "")}
        onOpenChange={setShowDeletingConfirmation}
      />
    </Form>
  );
};
